import ky from 'ky';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getSubsClientInstance } from '@/lib/utils/perps/subsClient.utils';
import { PerpsClearinghouseState, Subscription } from '@nktkas/hyperliquid';
const HYPER_LIQUID_INFO_ENDPOINT = 'https://api.hyperliquid.xyz/info';
type GetHLVaultInfoProps = {
  vaultAddress: string;
  userAddress?: string;
};
export type HLVaultInfoResult = {
  allowDeposits: boolean;
  alwaysCloseOnWithdraw: boolean;
  apr: number;
  description: string;
  followers: Follower[];
  followerState: FollowerState;
  isClosed: boolean;
  leader: string;
  leaderCommission: number;
  leaderFraction: number;
  maxDistributable: number;
  maxWithdrawable: number;
  name: string;
  portfolio: Array<[string, PortfolioObject]>;
  relationship: Relationship;
  vaultAddress: string;
};

export type FollowerState = {
  allTimePnl: string;
  daysFollowing: number;
  lockupUntil?: number;
  pnl: string;
  user: string;
  vaultEntryTime: number;
  vaultEquity: string;
};

export type Follower = {
  allTimePnl: string;
  daysFollowing: number;
  lockupUntil: number;
  pnl: string;
  user: string;
  vaultEntryTime: number;
  vaultEquity: string;
};

export type PortfolioObject = {
  accountValueHistory?: Array<Array<number | string>>;
  pnlHistory?: Array<Array<number | string>>;
  vlm?: string;
};

export type Relationship = {
  type: string;
};
export async function getHLVaultInfo({
  vaultAddress,
  userAddress,
}: GetHLVaultInfoProps) {
  const response = await ky.post(HYPER_LIQUID_INFO_ENDPOINT, {
    json: {
      vaultAddress,
      user: userAddress || undefined,
      type: 'vaultDetails',
    },
  });
  const data = await response.json<HLVaultInfoResult>();
  if (!response.ok) {
    throw new Error();
  }
  return data;
}
export type GetHLUserAssetsInVaultProps = {
  user: string;
};
export type HLUserVaultAssetsItem = {
  equity: string;
  lockedUntilTimestamp: number;
  vaultAddress: string;
};
export type HLUserAssetsInVaultResult = HLUserVaultAssetsItem[];
export async function getHLUserAssetsInVault({
  user,
}: GetHLUserAssetsInVaultProps) {
  const response = await ky.post(HYPER_LIQUID_INFO_ENDPOINT, {
    json: {
      user,
      type: 'userVaultEquities',
    },
  });
  const data = await response.json<HLUserAssetsInVaultResult>();
  if (!response.ok) {
    throw new Error();
  }
  return data;
}
// 定义 CumFunding 类型
type CumFunding = {
  allTime: string;
  sinceChange: string;
  sinceOpen: string;
};

// 定义 Leverage 类型
type Leverage = {
  rawUsd: string;
  type: string;
  value: number;
};

// 定义 Position 类型
type Position = {
  coin: string;
  cumFunding: CumFunding;
  entryPx: string;
  leverage: Leverage;
  liquidationPx: string;
  marginUsed: string;
  maxLeverage: number;
  positionValue: string;
  returnOnEquity: string;
  szi: string;
  unrealizedPnl: string;
};

// 定义 AssetPosition 类型
type AssetPosition = {
  position: Position;
  type: string;
};

// 定义 MarginSummary 类型
type MarginSummary = {
  accountValue: string;
  totalMarginUsed: string;
  totalNtlPos: string;
  totalRawUsd: string;
};

// 定义主类型

export type HLUserAccountSummary = {
  assetPositions: AssetPosition[];
  crossMaintenanceMarginUsed: string;
  crossMarginSummary: MarginSummary;
  marginSummary: MarginSummary;
  time: number;
  withdrawable: string;
};
export type GetHLUserAccountSummaryProps = {
  user: string;
};

export async function getHLUserAccountSummary({
  user,
}: GetHLUserAccountSummaryProps) {
  const response = await ky.post(HYPER_LIQUID_INFO_ENDPOINT, {
    json: {
      user,
      type: 'clearinghouseState',
    },
  });
  const data = await response.json<HLUserAccountSummary>();
  if (!response.ok) {
    throw new Error();
  }
  return data;
}
export function useHLAccountWs({ user }: GetHLUserAccountSummaryProps) {
  const [data, setData] = useState<PerpsClearinghouseState | null>(null);
  const unsubscribeRef = useRef<Subscription['unsubscribe'] | null>(null);
  const subUserInfo = useCallback(async (user: string) => {
    if (!user) {
      return;
    }
    const subsClient = getSubsClientInstance();
    if (unsubscribeRef.current) {
      unsubscribeRef.current?.();
    }
    const unsubscribe = await subsClient.webData2(
      {
        user: user as `0x${string}`,
      },
      (data) => {
        setData(data.clearinghouseState);
      },
    );
    unsubscribeRef.current = unsubscribe.unsubscribe;
  }, []);
  useEffect(() => {
    subUserInfo(user);
  }, [subUserInfo, user]);
  return data;
}
export function useHLAccountSummary({ user }: GetHLUserAccountSummaryProps) {
  return useQuery({
    queryKey: ['hlAccountSummary', user],
    queryFn: () => getHLUserAccountSummary({ user }),
    enabled: !!user && user !== '',
  });
}
export function useHLVaultInfo({
  vaultAddress,
  userAddress,
}: GetHLVaultInfoProps) {
  return useQuery({
    queryKey: ['hlVaultInfo', vaultAddress, userAddress],
    queryFn: () => getHLVaultInfo({ vaultAddress, userAddress }),
  });
}
type UseBatchVaultInfoProps = {
  batchParams: GetHLVaultInfoProps[];
};
export function useBatchVaultInfo({ batchParams }: UseBatchVaultInfoProps) {
  return useQuery({
    queryKey: ['batchVaultInfo', batchParams],
    queryFn: async (): Promise<Record<string, HLVaultInfoResult>> => {
      const batchRes = await Promise.allSettled(
        batchParams.map((params) => {
          return getHLVaultInfo(params);
        }),
      );
      const resObj: Record<string, HLVaultInfoResult> = {};
      batchRes.forEach((vaultInfo) => {
        if (vaultInfo.status === 'fulfilled') {
          resObj[vaultInfo.value.vaultAddress] = vaultInfo.value;
        }
      });
      return resObj;
    },
  });
}
export function useHLUserAssetsInVault({ user }: GetHLUserAssetsInVaultProps) {
  return useQuery({
    queryKey: ['hlUserAssetsInVault', user],
    queryFn: () => getHLUserAssetsInVault({ user }),
    enabled: !!user && user !== '',
  });
}
